/**
 * Screenata phase helpers.
 *
 * RFC-231 retired the onboarding phase: a workspace is born set up and the
 * Setup guide (`/guide`) owns everything the wizard used to ask, so there is
 * no redirect gate any more. `getCurrentStep` / `getScreenataPhase` survive
 * only as type-stable shims for the remaining callers; the chat-path helpers
 * below are still load-bearing.
 */

export type ScreenataPhase = 'onboarding' | 'main-menu'

export type OnboardingStep =
  | 'get-started'
  | 'connect-tools'
  | 'policy-prep'
  | 'scope-assessment'
  | 'complete'

/**
 * RFC-213 §6 — server-derived onboarding stage, from
 * `complianceProgram.getOnboardingStage`. The same shape `/v1/context` hands
 * the coding agent.
 */
export type OnboardingStage =
  | 'CONTEXT'
  | 'CONNECT'
  | 'SCAN'
  | 'ASSESS'
  | 'SCOPE'
  | 'SETUP'
  | 'COMPLETE'

/**
 * Resume point for the program-context wizard, derived from how far the
 * program actually got. Removed with the wizard by RFC-231 and restored with
 * it: the guide deep-links a specific `?step=`, but a bare `/onboarding`
 * still has to land somewhere sensible.
 */
export function stageToWizardStep(
  stage: OnboardingStage | undefined,
  completed?: { identityConnected?: boolean },
): string | undefined {
  switch (stage) {
    case 'CONNECT':
      return completed?.identityConnected ? 'connectCloud' : 'connectIdp'
    case 'SCAN':
      return 'connectCloud'
    case 'ASSESS':
    case 'SCOPE':
      return 'assessment'
    default:
      return undefined
  }
}

export interface ProgramData {
  contextQuestionCompletedAt?: Date | string | null
  connectToolsCompletedAt?: Date | string | null
  policyPrepCompletedAt?: Date | string | null
  testPlanConfirmedAt?: Date | string | null
}

export function getCurrentStep(
  program: ProgramData | null | undefined,
): OnboardingStep {
  if (!program?.contextQuestionCompletedAt) return 'get-started'
  return 'complete'
}

/**
 * A workspace whose company context has never been answered is in onboarding;
 * everything else is main-menu.
 *
 * RFC-231 stubbed this to always return `main-menu` when it retired the
 * wizard, which left the redirect in `index.lazy.tsx` intact but unreachable —
 * a brand-new workspace landed on chat with nothing asked. The wizard is back,
 * so the real check is back with it. This is a first-run redirect, not the old
 * phase gate: it stops the moment context is answered, and no other route is
 * ever blocked.
 */
export function getScreenataPhase(
  program: ProgramData | null | undefined,
): ScreenataPhase {
  return getCurrentStep(program) === 'complete' ? 'main-menu' : 'onboarding'
}

function parseWorkspaceSubpath(pathname: string): string | null {
  const match = pathname.match(/^\/workspaces\/[^/]+(\/.*)?$/)
  if (!match) return null
  return match[1] ?? ''
}

/**
 * True for the chat home (workspace root) and individual thread URLs.
 * Used by chat code that needs to know whether the current location is
 * a chat surface (e.g. for URL syncing on auto-thread-create).
 */
export function isWorkspaceChatPath(pathname: string): boolean {
  const subpath = parseWorkspaceSubpath(pathname)
  if (subpath === null) return false
  if (subpath === '' || subpath === '/') return true
  if (/^\/(?:screenata\/)?chat(\/|$)/.test(subpath)) return true
  return false
}

/**
 * Extract the workspace id from a URL like `/workspaces/<id>/...`.
 * Returns null when the path doesn't include a workspace segment.
 */
export function getWorkspaceIdFromPath(pathname: string): string | null {
  const m = pathname.match(/^\/workspaces\/([^/]+)/)
  return m?.[1] ?? null
}
